import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, LogOut } from "lucide-react";
import { getViewAsSession, clearViewAsSession, VIEW_AS_CHANGED_EVENT } from "@/lib/viewAsSession";
import { useEndViewAs } from "@/lib/coachQueries";

/**
 * Master v5 Prompt 17 — persistent View as Client banner. Mounted once,
 * globally, at the top of the whole /rehearsal-room tree (RehearsalRoomApp)
 * so it is impossible to be mid-View-as-Client without it — including
 * on every client-facing w/:workspaceId/* page the coach browses through.
 * Read-only is enforced server-side regardless; this banner's own job is
 * only to make that state impossible to miss, with a real countdown and
 * an always-available Exit.
 */
export default function ViewAsBanner() {
  const [session, setSession] = useState(() => getViewAsSession());
  const navigate = useNavigate();
  const endViewAs = useEndViewAs(session?.workspace_id);

  useEffect(() => {
    function sync() {
      setSession(getViewAsSession());
    }
    window.addEventListener(VIEW_AS_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    const tick = setInterval(sync, 1000);
    return () => {
      window.removeEventListener(VIEW_AS_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
      clearInterval(tick);
    };
  }, []);

  if (!session) return null;

  const remainingMs = new Date(session.expires_at).getTime() - Date.now();
  const remainingMin = Math.max(0, Math.floor(remainingMs / 60000));
  const remainingSec = Math.max(0, Math.floor((remainingMs % 60000) / 1000));

  async function handleExit() {
    try {
      await endViewAs.mutateAsync(session.vas_id);
    } catch {
      // Best-effort: even if /end 404s (already expired), the client
      // must still be dropped back to their own coach-perspective view.
    }
    clearViewAsSession();
    navigate(`/rehearsal-room/coach/clients`);
  }

  return (
    <div
      className="sticky top-0 z-50 flex items-center justify-between gap-3 px-4 sm:px-6 py-2.5 bg-mustard text-ink border-b border-ink/20"
      data-testid="view-as-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Eye size={16} className="shrink-0" />
        <p className="text-sm font-medium truncate">
          Viewing as <span data-testid="view-as-banner-target-name">{session.target_member_name}</span>
          <span className="hidden sm:inline"> · {session.workspace_title}</span> · Read-only
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-xs text-ink/70 tabular-nums" data-testid="view-as-banner-countdown">
          {remainingMin}:{String(remainingSec).padStart(2, "0")}
        </span>
        <button
          onClick={handleExit}
          className="flex items-center gap-1.5 text-sm font-medium bg-ink text-paper px-3 py-1 rounded-full hover:bg-ink/85 transition-colors"
          data-testid="view-as-banner-exit"
        >
          <LogOut size={13} /> Exit
        </button>
      </div>
    </div>
  );
}
